/**
 * The kinds a new classification can be, described once: what the admin picks
 * from in New type, what a card and a type page call its values, and what an
 * author does with it. Settings and the question editor both read this.
 */

import type { ClassificationKind } from "@/lib/classification/lookups"

/** A kind in New type: the four custom kinds, and Standard, which adds to Standards. */
export type NewTypeKind = ClassificationKind | "standard"

export interface KindCopy {
  label: string
  icon: string
  /** A type it is like, so the choice reads at a glance. */
  like: string
  /** What an author does with it. */
  pick: string
  /** "level", "option", "tag", "category". */
  noun: string
  nouns: string
  /** The empty state's one sentence. */
  start: string
}

export const KIND_COPY: Record<NewTypeKind, KindCopy> = {
  scale: {
    label: "Scale",
    icon: "fa-signal-bars",
    like: "Like Bloom's or Difficulty",
    pick: "Ordered levels. Authors pick one.",
    noun: "level",
    nouns: "levels",
    start: "Add the levels authors choose from, lowest first. Each one is offered to authors as soon as you add it.",
  },
  choice: {
    label: "Single choice",
    icon: "fa-circle-dot",
    like: "Like question source",
    pick: "Options in no order. Authors pick one.",
    noun: "option",
    nouns: "options",
    start: "Add the options authors choose one from. Each one is offered to authors as soon as you add it.",
  },
  tags: {
    label: "Tags",
    icon: "fa-tags",
    like: "Like clinical setting or body system",
    pick: "Authors pick as many as apply.",
    noun: "tag",
    nouns: "tags",
    start: "Add the tags authors can put on a question, as many as apply. Each one is offered as soon as you add it.",
  },
  tree: {
    label: "Hierarchy",
    icon: "fa-sitemap",
    like: "Like Content",
    pick: "Categories with sub-categories. Authors pick from any branch.",
    noun: "category",
    nouns: "categories",
    start: "Add a top-level category, then nest sub-categories under it. Authors can tag questions with it as soon as the first one is in.",
  },
  standard: {
    label: "Standard",
    icon: "fa-award",
    like: "Like NCLEX or program competencies",
    pick: "A framework the program reports on, mapped to course content.",
    noun: "category",
    nouns: "categories",
    start: "",
  },
}

/**
 * The kinds a type can switch between after it is made: any of them. Scale,
 * single choice and tags share their levels, so switching among them changes
 * only how they read and how authors pick. To or from a hierarchy the values
 * are carried across (see lib/classification/convert). A standard is not a
 * type at all, so it does not switch.
 */
export const SWITCHABLE_KINDS: readonly ClassificationKind[] = ["scale", "choice", "tags", "tree"]

/** What switching from one list kind to another does to what is already there. */
export function switchNote(from: ClassificationKind, to: ClassificationKind, restored = false): string | null {
  if (from === to) return null
  if (to === "tree")
    return restored
      ? "Its earlier tree is back. Questions tagged in it keep those tags."
      : `Each ${KIND_COPY[from].noun} is now a top-level category. Add sub-categories inside them.`
  if (from === "tree")
    return restored
      ? `Its earlier ${KIND_COPY[to].nouns} are back. The tree is kept, hidden, until it is a hierarchy again.`
      : `Each category is now a ${KIND_COPY[to].noun}, named by its path. The tree is kept, hidden, until it is a hierarchy again.`
  if (from === "tags") return "Questions that carry more than one tag keep them until an author next edits the question."
  if (from === "scale") return "Values and places are kept, hidden until it is a scale again."
  if (to === "scale") return "Its options become levels in their current order. Give each a value on the list."
  return null
}

/** The order New type lists them in. */
export const NEW_TYPE_KINDS: readonly NewTypeKind[] = ["scale", "choice", "tags", "tree", "standard"]
